import { BaseBrandService, BrandCreateInput, BrandUpdateInput, BrandQueryOptions, BrandServiceResponse } from './brandService'
import { ParsedBrand, Brand } from '../types/brand'
import { loadBrandsFromCSV } from '../utils/csvLoader'

function parseSemicolonDelimited(value: string): string[] {
  if (!value || value === 'none' || value === 'unknown') return []
  return value.split(';').map(item => item.trim()).filter(item => item.length > 0)
}

function parseCommaDelimited(value: string): string[] {
  if (!value || value === 'none' || value === 'unknown') return []
  return value.split(',').map(item => item.trim()).filter(item => item.length > 0)
}

function extractStateFromLocation(location: string): string | undefined {
  const US_STATES = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ]
  
  if (!location || location === 'unknown') return undefined
  
  const foundState = US_STATES.find(state => 
    location.toLowerCase().includes(state.toLowerCase())
  )
  
  return foundState
}

function convertInputToParsedBrand(input: BrandCreateInput): ParsedBrand {
  const brand: Brand = {
    ...input,
    last_verified: input.last_verified || new Date().toISOString()
  }
  
  const parsedBrand: ParsedBrand = {
    ...brand,
    product_categories: parseSemicolonDelimited(brand.product_categories),
    clean_categories: parseCommaDelimited(brand.clean_categories),
    mfg_locations: parseSemicolonDelimited(brand.mfg_locations),
    certifications: parseSemicolonDelimited(brand.certifications),
    hq_state: extractStateFromLocation(brand.hq_location),
    founded_decade: typeof brand.founded_year === 'number' ? Math.floor(brand.founded_year / 10) * 10 : undefined
  }

  return parsedBrand
}

export class LocalBrandService extends BaseBrandService {
  private brands: ParsedBrand[] = []
  private isLoaded = false
  
  private async ensureLoaded(): Promise<void> {
    if (!this.isLoaded) {
      try {
        this.brands = await loadBrandsFromCSV()
        this.isLoaded = true
      } catch (error) {
        throw new Error(`Failed to load brands data: ${error}`)
      }
    }
  }
  
  async getAllBrands(options?: BrandQueryOptions): Promise<BrandServiceResponse<ParsedBrand[]>> {
    try {
      await this.ensureLoaded()
      
      let result = [...this.brands]
      
      // Apply search filter
      if (options?.search) {
        const searchLower = options.search.toLowerCase()
        result = result.filter(brand => 
          brand.brand.toLowerCase().includes(searchLower) ||
          brand.consumer_summary.toLowerCase().includes(searchLower) ||
          brand.primary_products.toLowerCase().includes(searchLower) ||
          brand.clean_categories.some(cat => cat.toLowerCase().includes(searchLower))
        )
      }
      
      // Apply custom filters
      if (options?.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            result = result.filter(brand => {
              const brandValue = (brand as any)[key]
              if (Array.isArray(value)) {
                return Array.isArray(brandValue) 
                  ? value.some(v => brandValue.includes(v))
                  : value.includes(brandValue)
              }
              return brandValue === value
            })
          }
        })
      }
      
      // Apply pagination
      const total = result.length
      if (options?.offset !== undefined) {
        result = result.slice(options.offset)
      }
      if (options?.limit !== undefined) {
        result = result.slice(0, options.limit)
      }
      
      return {
        data: result,
        success: true,
        total
      }
    } catch (error) {
      return {
        data: [],
        success: false,
        error: `Failed to get brands: ${error}`
      }
    }
  }
  
  async getBrandByName(brandName: string): Promise<BrandServiceResponse<ParsedBrand | null>> {
    try {
      await this.ensureLoaded()
      
      const brand = this.brands.find(b => b.brand === brandName)
      
      return {
        data: brand || null,
        success: true
      }
    } catch (error) {
      return {
        data: null,
        success: false,
        error: `Failed to get brand: ${error}`
      }
    }
  }
  
  async createBrand(brand: BrandCreateInput): Promise<BrandServiceResponse<ParsedBrand>> {
    try {
      await this.ensureLoaded()
      
      // Check if brand already exists
      const existing = this.brands.find(b => b.brand === brand.brand)
      if (existing) {
        return {
          data: existing,
          success: false,
          error: `Brand with name '${brand.brand}' already exists`
        }
      }
      
      // Validate brand data
      const validation = await this.validateBrand(brand)
      if (!validation.success) {
        return {
          data: convertInputToParsedBrand(brand),
          success: false,
          error: validation.error
        }
      }
      
      const parsedBrand = convertInputToParsedBrand(brand)
      this.brands.push(parsedBrand)
      
      // In a real implementation, you'd save to persistent storage here
      console.log(`[LocalBrandService] Created brand: ${brand.brand}`)
      
      return {
        data: parsedBrand,
        success: true
      }
    } catch (error) {
      return {
        data: convertInputToParsedBrand(brand),
        success: false,
        error: `Failed to create brand: ${error}`
      }
    }
  }
  
  async updateBrand(brandName: string, updates: BrandUpdateInput): Promise<BrandServiceResponse<ParsedBrand>> {
    try {
      await this.ensureLoaded()
      
      const brandIndex = this.brands.findIndex(b => b.brand === brandName)
      if (brandIndex === -1) {
        return {
          data: convertInputToParsedBrand({ ...updates, brand: brandName } as BrandCreateInput),
          success: false,
          error: `Brand with name '${brandName}' not found`
        }
      }
      
      const currentBrand = this.brands[brandIndex]
      if (!currentBrand) {
        return {
          data: convertInputToParsedBrand({ ...updates, brand: brandName } as BrandCreateInput),
          success: false,
          error: `Brand with name '${brandName}' not found`
        }
      }
      
      // Merge updates with current brand data
      const updatedBrandInput: BrandCreateInput = {
        brand: updates.brand || currentBrand.brand,
        website: updates.website !== undefined ? updates.website : currentBrand.website,
        amazon_presence: updates.amazon_presence || currentBrand.amazon_presence,
        manufacturing_verification: updates.manufacturing_verification || currentBrand.manufacturing_verification,
        hq_location: updates.hq_location !== undefined ? updates.hq_location : currentBrand.hq_location,
        mfg_locations: updates.mfg_locations !== undefined ? updates.mfg_locations : currentBrand.mfg_locations.join(';'),
        primary_products: updates.primary_products !== undefined ? updates.primary_products : currentBrand.primary_products,
        product_categories: updates.product_categories !== undefined ? updates.product_categories : currentBrand.product_categories.join(';'),
        clean_categories: updates.clean_categories !== undefined ? updates.clean_categories : currentBrand.clean_categories.join(','),
        purchase_methods: updates.purchase_methods !== undefined ? updates.purchase_methods : currentBrand.purchase_methods,
        founded_year: updates.founded_year !== undefined ? updates.founded_year : currentBrand.founded_year,
        business_type: updates.business_type !== undefined ? updates.business_type : currentBrand.business_type,
        certifications: updates.certifications !== undefined ? updates.certifications : currentBrand.certifications.join(';'),
        price_tier: updates.price_tier || currentBrand.price_tier,
        consumer_summary: updates.consumer_summary !== undefined ? updates.consumer_summary : currentBrand.consumer_summary,
        evidence_sources: updates.evidence_sources !== undefined ? updates.evidence_sources : currentBrand.evidence_sources,
        confidence_score: updates.confidence_score !== undefined ? updates.confidence_score : currentBrand.confidence_score,
        last_verified: new Date().toISOString()
      }
      
      // Validate updated brand data
      const validation = await this.validateBrand(updatedBrandInput)
      if (!validation.success) {
        return {
          data: convertInputToParsedBrand(updatedBrandInput),
          success: false,
          error: validation.error
        }
      }
      
      const updatedParsedBrand = convertInputToParsedBrand(updatedBrandInput)
      this.brands[brandIndex] = updatedParsedBrand
      
      // In a real implementation, you'd save to persistent storage here
      console.log(`[LocalBrandService] Updated brand: ${brandName}`)
      
      return {
        data: updatedParsedBrand,
        success: true
      }
    } catch (error) {
      return {
        data: convertInputToParsedBrand({ ...updates, brand: brandName } as BrandCreateInput),
        success: false,
        error: `Failed to update brand: ${error}`
      }
    }
  }
  
  async deleteBrand(brandName: string): Promise<BrandServiceResponse<boolean>> {
    try {
      await this.ensureLoaded()
      
      const brandIndex = this.brands.findIndex(b => b.brand === brandName)
      if (brandIndex === -1) {
        return {
          data: false,
          success: false,
          error: `Brand with name '${brandName}' not found`
        }
      }
      
      this.brands.splice(brandIndex, 1)
      
      // In a real implementation, you'd save to persistent storage here
      console.log(`[LocalBrandService] Deleted brand: ${brandName}`)
      
      return {
        data: true,
        success: true
      }
    } catch (error) {
      return {
        data: false,
        success: false,
        error: `Failed to delete brand: ${error}`
      }
    }
  }
  
  async refreshData(): Promise<BrandServiceResponse<boolean>> {
    try {
      this.brands = await loadBrandsFromCSV(`/brands.csv?t=${Date.now()}`)
      this.isLoaded = true
      
      console.log(`[LocalBrandService] Refreshed data: ${this.brands.length} brands loaded`)
      
      return {
        data: true,
        success: true
      }
    } catch (error) {
      return {
        data: false,
        success: false,
        error: `Failed to refresh data: ${error}`
      }
    }
  }
  
  // Additional utility methods specific to local service
  getBrandCount(): number {
    return this.brands.length
  }
  
  getInMemoryBrands(): ParsedBrand[] {
    return [...this.brands]
  }
  
  async saveToLocalStorage(): Promise<void> {
    try {
      const data = JSON.stringify(this.brands)
      localStorage.setItem('brandscope_brands_backup', data)
      console.log('[LocalBrandService] Saved backup to localStorage')
    } catch (error) {
      console.error('[LocalBrandService] Failed to save to localStorage:', error)
    }
  }
  
  async loadFromLocalStorage(): Promise<boolean> {
    try {
      const data = localStorage.getItem('brandscope_brands_backup')
      if (data) {
        this.brands = JSON.parse(data)
        this.isLoaded = true
        console.log(`[LocalBrandService] Loaded ${this.brands.length} brands from localStorage`)
        return true
      }
      return false
    } catch (error) {
      console.error('[LocalBrandService] Failed to load from localStorage:', error)
      return false
    }
  }
}